import { KTSVG } from '../../../_metronic/helpers'
import React, { FC, useRef, useState } from 'react'
import { Modal } from 'react-bootstrap'
import * as XLSX from 'xlsx'
import { useAuth } from '../../../app/modules/auth'
import { updateProducts } from '../../apis/Loadalltabledata'

type Props = {
  show: boolean
  handleClose: () => void
  existingProducts: any[]
  allproductsrefetch: () => void
  sumstockrefetch: () => void
  variancerefetch: () => void
}

interface ImportRow {
  product_id: number
  product_name: string
  current_stock: number
  new_stock: number
  unit_buying_price: number
  unit_selling_price: number
  matched: boolean
  rowIndex: number
}

const CURRENCY_APP = process.env.REACT_APP_CURRENCY || 'KES'

const REQUIRED_COLUMNS = ['product_name', 'units_in_stock']
const OPTIONAL_COLUMNS = ['unit_buying_price', 'unit_selling_price']

export const BulkStockImport: FC<Props> = ({
  show,
  handleClose,
  existingProducts,
  allproductsrefetch,
  sumstockrefetch,
  variancerefetch,
}) => {
  const { currentUser } = useAuth()
  const fileInputRef = useRef<HTMLInputElement>(null)
  const [fileName, setFileName] = useState<string>('')
  const [importRows, setImportRows] = useState<ImportRow[]>([])
  const [parseError, setParseError] = useState<string>('')
  const [loading, setLoading] = useState(false)
  const [step, setStep] = useState<'upload' | 'preview'>('upload')

  // Build a normalised name → product lookup for fast matching
  const productByName = React.useMemo(() => {
    const map = new Map<string, any>()
    existingProducts.forEach((p) => {
      map.set(p.product_name?.trim().toLowerCase(), p)
    })
    return map
  }, [existingProducts])

  // ── Template download ────────────────────────────────────────────────────
  const downloadTemplate = () => {
    const rows = existingProducts
      .slice()
      .sort((a, b) => (a.product_name || '').localeCompare(b.product_name || ''))
      .map((p) => ({
        product_name: p.product_name,
        units_in_stock: p.units_in_stock,
        unit_buying_price: p.unit_buying_price,
        unit_selling_price: p.unit_selling_price,
      }))

    const ws = XLSX.utils.json_to_sheet(rows)
    // Column widths
    ws['!cols'] = [{ wch: 35 }, { wch: 18 }, { wch: 20 }, { wch: 20 }]
    const wb = XLSX.utils.book_new()
    XLSX.utils.book_append_sheet(wb, ws, 'Stock Import')
    const buf = XLSX.write(wb, { bookType: 'xlsx', type: 'array' })
    const blob = new Blob([buf], { type: 'application/octet-stream' })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = 'stock_import_template.xlsx'
    document.body.appendChild(a)
    a.click()
    document.body.removeChild(a)
    URL.revokeObjectURL(url)
  }

  // ── File parsing ─────────────────────────────────────────────────────────
  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (!file) return

    setFileName(file.name)
    setParseError('')
    setImportRows([])

    const ext = file.name.split('.').pop()?.toLowerCase()
    if (!['csv', 'xlsx', 'xls'].includes(ext || '')) {
      setParseError('Unsupported file type. Please upload a .csv, .xlsx or .xls file.')
      return
    }

    const reader = new FileReader()
    reader.onload = (event) => {
      try {
        const data = new Uint8Array(event.target?.result as ArrayBuffer)
        const wb = XLSX.read(data, { type: 'array' })
        const ws = wb.Sheets[wb.SheetNames[0]]
        const raw: any[] = XLSX.utils.sheet_to_json(ws, { defval: '' })

        if (raw.length === 0) {
          setParseError('The file is empty or has no data rows.')
          return
        }

        // Normalise column names (trim + lowercase)
        const normalised = raw.map((row) => {
          const out: Record<string, any> = {}
          Object.keys(row).forEach((k) => {
            out[k.trim().toLowerCase().replace(/\s+/g, '_')] = row[k]
          })
          return out
        })

        // Validate required columns
        const firstRow = normalised[0]
        for (const col of REQUIRED_COLUMNS) {
          if (!(col in firstRow)) {
            setParseError(
              `Missing required column: "${col}". Required columns are: ${REQUIRED_COLUMNS.join(', ')}`
            )
            return
          }
        }

        // Map rows to ImportRow
        const mapped: ImportRow[] = normalised.map((row, i) => {
          const name = String(row['product_name'] || '').trim()
          const match = productByName.get(name.toLowerCase())
          const newStock = Number(row['units_in_stock'])

          return {
            product_id: match?.product_id ?? -1,
            product_name: name,
            current_stock: match?.units_in_stock ?? 0,
            new_stock: isNaN(newStock) ? 0 : newStock,
            unit_buying_price:
              row['unit_buying_price'] !== ''
                ? Number(row['unit_buying_price'])
                : match?.unit_buying_price ?? 0,
            unit_selling_price:
              row['unit_selling_price'] !== ''
                ? Number(row['unit_selling_price'])
                : match?.unit_selling_price ?? 0,
            matched: !!match,
            rowIndex: i + 2, // 1-based + header row
          }
        })

        setImportRows(mapped)
        setStep('preview')
      } catch (err) {
        setParseError('Failed to parse the file. Make sure it is a valid CSV or Excel file.')
      }
    }
    reader.readAsArrayBuffer(file)
  }

  // ── Submit ───────────────────────────────────────────────────────────────
  const handleSubmit = async () => {
    const matched = importRows.filter((r) => r.matched)
    if (matched.length === 0) return

    setLoading(true)

    const valuestoupdate = matched.map((r) => ({
      product_id: r.product_id,
      user_id: currentUser?.user_id,
      // Send the delta so the backend's increment logic sets stock to the correct absolute value
      units_in_stock: r.new_stock - r.current_stock,
      unit_buying_price: r.unit_buying_price,
      unit_selling_price: r.unit_selling_price,
    }))

    try {
      await updateProducts(valuestoupdate)
      setLoading(false)
      allproductsrefetch()
      sumstockrefetch()
      variancerefetch()
      resetState()
      handleClose()
    } catch (error: any) {
      console.error('Bulk stock import error:', error)
      setParseError('Failed to save changes. Please try again.')
      setLoading(false)
    }
  }

  // ── Reset ────────────────────────────────────────────────────────────────
  const resetState = () => {
    setFileName('')
    setImportRows([])
    setParseError('')
    setStep('upload')
    if (fileInputRef.current) fileInputRef.current.value = ''
  }

  const onHide = () => {
    if (!loading) {
      resetState()
      handleClose()
    }
  }

  const matchedCount = importRows.filter((r) => r.matched).length
  const unmatchedCount = importRows.length - matchedCount

  return (
    <Modal
      className='modal fade'
      show={show}
      onHide={onHide}
      centered
      dialogClassName='modal-xl'
      data-backdrop='static'
      tabIndex={-1}
    >
      <div className='modal-content'>
        <div className='modal-header'>
          <h3 className='fw-bolder'>
            Bulk Stock Import
            {step === 'preview' && importRows.length > 0 && (
              <span className='text-muted fw-bold fs-6 ms-3'>
                {matchedCount} matched · {unmatchedCount} unmatched
              </span>
            )}
          </h3>
          <div className='btn btn-icon btn-sm btn-active-light-primary ms-2' onClick={onHide}>
            <KTSVG path='/media/icons/duotune/arrows/arr061.svg' className='svg-icon-2x' />
          </div>
        </div>

        <div className='modal-body'>
          {/* ── Step 1: Upload ── */}
          {step === 'upload' && (
            <>
              {/* Instructions */}
              <div className='alert alert-primary d-flex align-items-center mb-7'>
                <KTSVG
                  path='/media/icons/duotune/general/gen044.svg'
                  className='svg-icon-2x svg-icon-primary me-3'
                />
                <div>
                  <div className='fw-bold'>How to use</div>
                  <ol className='mb-0 mt-1 ps-4'>
                    <li>Download the template — it is pre-filled with all your current products and stock levels.</li>
                    <li>Edit the <strong>units_in_stock</strong> column (and optionally prices) in Excel or Google Sheets.</li>
                    <li>Save as <strong>.xlsx</strong> or <strong>.csv</strong> and upload it here.</li>
                  </ol>
                </div>
              </div>

              {/* Template download */}
              <div className='fv-row mb-7'>
                <button
                  type='button'
                  className='btn btn-light-success me-3'
                  onClick={downloadTemplate}
                  disabled={existingProducts.length === 0}
                >
                  <KTSVG path='/media/icons/duotune/files/fil002.svg' className='svg-icon-2' />
                  Download Template ({existingProducts.length} products)
                </button>
              </div>

              {/* File input */}
              <div className='fv-row mb-4'>
                <label className='required fw-bold fs-6 mb-2'>Upload File</label>
                <input
                  ref={fileInputRef}
                  type='file'
                  accept='.csv,.xlsx,.xls'
                  className='form-control form-control-solid'
                  onChange={handleFileChange}
                />
                <div className='form-text text-muted mt-1'>
                  Supported formats: .csv, .xlsx, .xls
                </div>
              </div>

              {parseError && (
                <div className='alert alert-danger mt-3'>
                  <span className='fw-bold'>Error: </span>{parseError}
                </div>
              )}
            </>
          )}

          {/* ── Step 2: Preview ── */}
          {step === 'preview' && (
            <>
              {parseError && (
                <div className='alert alert-danger mb-5'>
                  <span className='fw-bold'>Error: </span>{parseError}
                </div>
              )}

              {unmatchedCount > 0 && (
                <div className='alert alert-warning d-flex align-items-center mb-5'>
                  <KTSVG
                    path='/media/icons/duotune/general/gen044.svg'
                    className='svg-icon-2x svg-icon-warning me-3'
                  />
                  <div>
                    <strong>{unmatchedCount} row{unmatchedCount !== 1 ? 's' : ''} could not be matched</strong> to an existing product and will be skipped.
                    Check that the <code>product_name</code> spelling matches exactly.
                  </div>
                </div>
              )}

              <div className='table-responsive' style={{ maxHeight: '55vh', overflowY: 'auto' }}>
                <table className='table table-bordered table-hover align-middle gs-3 gy-3 fw-bold'>
                  <thead className='text-uppercase fs-7 text-gray-400' style={{ position: 'sticky', top: 0, background: '#fff', zIndex: 1 }}>
                    <tr>
                      <th>Row</th>
                      <th>Product Name</th>
                      <th className='text-end'>Current Stock</th>
                      <th className='text-end'>New Stock</th>
                      <th className='text-end'>Δ Stock</th>
                      <th className='text-end'>Buy Price ({CURRENCY_APP})</th>
                      <th className='text-end'>Sell Price ({CURRENCY_APP})</th>
                      <th className='text-center'>Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {importRows.map((row) => {
                      const delta = row.new_stock - row.current_stock
                      return (
                        <tr
                          key={row.rowIndex}
                          className={row.matched ? '' : 'table-danger opacity-75'}
                        >
                          <td className='text-muted fs-7'>{row.rowIndex}</td>
                          <td>{row.product_name || <span className='text-danger'>(empty)</span>}</td>
                          <td className='text-end'>{row.matched ? row.current_stock : '—'}</td>
                          <td className='text-end fw-bolder'>{row.new_stock}</td>
                          <td className='text-end'>
                            {row.matched ? (
                              <span className={delta > 0 ? 'text-success' : delta < 0 ? 'text-danger' : 'text-muted'}>
                                {delta > 0 ? '+' : ''}{delta}
                              </span>
                            ) : '—'}
                          </td>
                          <td className='text-end'>{row.unit_buying_price}</td>
                          <td className='text-end'>{row.unit_selling_price}</td>
                          <td className='text-center'>
                            {row.matched ? (
                              <span className='badge badge-light-success'>Matched</span>
                            ) : (
                              <span className='badge badge-light-danger'>Not found</span>
                            )}
                          </td>
                        </tr>
                      )
                    })}
                  </tbody>
                </table>
              </div>

              <div className='mt-4 d-flex justify-content-between align-items-center'>
                <span className='text-muted fs-7'>
                  {matchedCount} product{matchedCount !== 1 ? 's' : ''} will be updated.
                  {unmatchedCount > 0 && ` ${unmatchedCount} will be skipped.`}
                </span>
                <button
                  type='button'
                  className='btn btn-light btn-sm'
                  onClick={() => {
                    setStep('upload')
                    setImportRows([])
                    setParseError('')
                    if (fileInputRef.current) fileInputRef.current.value = ''
                  }}
                >
                  ← Upload different file
                </button>
              </div>
            </>
          )}
        </div>

        <div className='modal-footer border-0 mt-n5'>
          <button type='button' className='btn btn-light-primary' onClick={onHide} disabled={loading}>
            Cancel
          </button>

          {step === 'preview' && (
            <button
              type='button'
              className='btn btn-success'
              onClick={handleSubmit}
              disabled={loading || matchedCount === 0}
            >
              {loading ? (
                <span className='indicator-progress' style={{ display: 'block' }}>
                  Saving...
                  <span className='spinner-border spinner-border-sm align-middle ms-2' />
                </span>
              ) : (
                `Apply Changes (${matchedCount} product${matchedCount !== 1 ? 's' : ''})`
              )}
            </button>
          )}
        </div>
      </div>
    </Modal>
  )
}
